import { Box, Text } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useEffect, useState } from 'react';

import type { TextProps } from '@chakra-ui/react';

const SKELETON_MS = 250;
const FADE_MS = 350;
const IOS_CURVE = [0.32, 0.72, 0, 1] as const;

interface FieldDiffProps extends TextProps {
  /** Stable field key matched against `changedFields` from useCartFieldDiff. */
  fieldKey: string;
  /** The set of changed field keys. */
  changedFields: Set<string>;
  /** Rendered text. */
  children: ReactNode;
  /** Skeleton width fallback (when content briefly hides). */
  skeletonWidth?: string;
}

/**
 * Render `children` as a <Text>. If `fieldKey` enters `changedFields`,
 * briefly show a quiet skeleton bar (250 ms) then crossfade to the new
 * content (350 ms, iOS curve). Matches the rev-5/6 visual vocabulary.
 *
 * Defaults to a normal <Text> render — zero overhead when nothing
 * changed.
 */
export const FieldDiff = ({
  fieldKey,
  changedFields,
  children,
  skeletonWidth = '3rem',
  ...textProps
}: FieldDiffProps) => {
  const isChanged = changedFields.has(fieldKey);
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (!isChanged) return;
    setShowSkeleton(true);
    const t = setTimeout(() => setShowSkeleton(false), SKELETON_MS);
    return () => clearTimeout(t);
  }, [isChanged, children]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      {showSkeleton ? (
        <motion.span
          key="skeleton"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          exit={{ opacity: 0 }}
          transition={{ duration: FADE_MS / 1000, ease: IOS_CURVE }}
          style={{ display: 'inline-block' }}
        >
          <Box
            display="inline-block"
            verticalAlign="middle"
            bg="var(--border, #E0E0E0)"
            h="0.875rem"
            w={skeletonWidth}
            borderRadius="0.25rem"
          />
        </motion.span>
      ) : (
        <motion.span
          key="value"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: FADE_MS / 1000, ease: IOS_CURVE }}
          style={{ display: 'inline-block' }}
        >
          <Text as="span" {...textProps}>
            {children}
          </Text>
        </motion.span>
      )}
    </AnimatePresence>
  );
};
